/**
 * Pause Enrollment Dialog (iter271 — extracted from Enrollments.js).
 */
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Pause, PlayCircle, Calendar, CalendarClock } from 'lucide-react';

// iter307ey — Given the current next_billing_date and a pause window,
// compute the auto-shifted NBD. Mirrors the backend's iter300 logic:
// shift by rounded calendar months of the pause duration.
function computeAutoNBD(currentNBD, pauseDate, resumeDate) {
  if (!currentNBD || !pauseDate || !resumeDate) return '';
  try {
    const pd = new Date(pauseDate);
    const rd = new Date(resumeDate);
    const nbd = new Date(currentNBD);
    if (isNaN(pd) || isNaN(rd) || isNaN(nbd)) return '';
    const pauseDays = Math.round((rd - pd) / (1000 * 60 * 60 * 24));
    if (pauseDays <= 0) return '';
    const monthsPaused = Math.max(1, Math.round(pauseDays / 30));
    const shifted = new Date(nbd);
    shifted.setMonth(shifted.getMonth() + monthsPaused);
    return shifted.toISOString().slice(0, 10);
  } catch { return ''; }
}

export default function PauseEnrollmentDialog({
  open,
  onOpenChange,
  selectedEnrollment,
  actionData,
  setActionData,
  onConfirm,
}) {
  const currentNBD = selectedEnrollment?.next_billing_date
    ? String(selectedEnrollment.next_billing_date).slice(0, 10)
    : '';
  const autoNBD = computeAutoNBD(currentNBD, actionData.pause_date, actionData.auto_resume_date);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Pause className="h-5 w-5 text-amber-500" />
            Pause Enrollment
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="font-medium text-slate-900">{selectedEnrollment?.player_name}</p>
            <p className="text-sm text-slate-500">Program: {selectedEnrollment?.program_name}</p>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-amber-500" />
              Pause Effective Date *
            </Label>
            <Input
              type="date"
              value={actionData.pause_date}
              onChange={(e) => setActionData({ ...actionData, pause_date: e.target.value })}
              data-testid="pause-date-input"
            />
            <p className="text-xs text-slate-500">When should this enrollment be paused?</p>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <PlayCircle className="h-4 w-4 text-green-500" />
              Auto-Resume Date (Optional)
            </Label>
            <Input
              type="date"
              value={actionData.auto_resume_date}
              onChange={(e) => setActionData({ ...actionData, auto_resume_date: e.target.value })}
              min={actionData.pause_date}
              data-testid="auto-resume-date-input"
            />
            <p className="text-xs text-slate-500">Leave empty for manual resume. Set a date to auto-resume.</p>
          </div>

          {/* iter307ey — Next Billing Date preview + override */}
          <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <CalendarClock className="h-4 w-4 text-blue-500" />
              Next Billing Date
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">Current NBD:</span>
              <span className="font-mono text-slate-900" data-testid="pause-current-nbd">
                {currentNBD || '—'}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">Auto-computed after pause:</span>
              <span className="font-mono text-emerald-700" data-testid="pause-auto-nbd">
                {autoNBD || '—'}
              </span>
            </div>
            <div className="mt-2">
              <Label className="text-xs text-slate-600">
                Override NBD (leave blank to use auto-computed)
              </Label>
              <Input
                type="date"
                value={actionData.next_billing_date || ''}
                onChange={(e) => setActionData({ ...actionData, next_billing_date: e.target.value })}
                min={actionData.pause_date || undefined}
                data-testid="pause-nbd-override-input"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Bi-annual example: enrollment through Aug 31 (NBD Sep 1),
                pause Aug 1–31 → auto-shifts NBD to Oct 1.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes (Optional)</Label>
            <Textarea
              value={actionData.notes}
              onChange={(e) => setActionData({ ...actionData, notes: e.target.value })}
              placeholder="Reason for pausing..."
              data-testid="pause-notes-input"
            />
          </div>

          <DialogFooter className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600"
              onClick={onConfirm}
              data-testid="confirm-pause-btn"
            >
              <Pause className="h-4 w-4 mr-1" />
              Pause Enrollment
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
